import { createContext } from "react";
import { getReview } from "../../../api/content/reviewsApi";
import { commitToChangeSet, type ChangeSetChoice, type CommitBuilder } from "../../changeSets/changeSetChoice";

/**
 * What a save did: nothing to change; the change set an edit step was answered with, or whose
 * review the edit joined, in the Thread that reviews it; or, outside an edit step, only the
 * change set.
 */
export type StepSave =
  | { readonly kind: "nothingToChange" }
  | { readonly kind: "answered"; readonly changeSetId: string; readonly threadId: string }
  | { readonly kind: "committed"; readonly changeSetId: string };

/**
 * An editor shown as the "Edit an item" step's view (ADR-0031 D2): saving commits the edits
 * into the chosen change set and answers the step with it, and the step's Thread reviews it.
 * The editors don't know where they are shown: in the Thread the step waits; before it, the
 * first save starts the Thread (WA-11: opening the editor creates nothing).
 */
export interface ChangeSetStep {
  readonly save: (projectId: string, choice: ChangeSetChoice, builders: readonly CommitBuilder[]) => Promise<StepSave>;
  /** The Thread whose page shows the step, so its review follows right here; `null` elsewhere. */
  readonly shownIn: string | null;
}

/**
 * The one save of an edit step: commit, then `answer`, which names the Thread that reviews the
 * change set. An open change set chosen for the edit that a Thread already reviews takes the
 * edit into that Thread's review, since a change set has one review subject (ADR-0026 D3):
 * the step then answers nothing. A save whose answer failed made its change set, so the next
 * save commits into it and answers with it, never a second one.
 */
export function answeringStep(answer: (changeSetId: string) => Promise<string>, shownIn: string | null): ChangeSetStep {
  let unanswered: string | null = null;
  return {
    shownIn,
    save: async (projectId, choice, builders) => {
      const target: ChangeSetChoice = unanswered === null ? choice : { kind: "existing", changeSetId: unanswered };
      const reviewedIn = unanswered === null && target.kind === "existing" ? ((await getReview(target.changeSetId)).reviewSubject?.threadId ?? null) : null;
      const outcome = await commitToChangeSet(projectId, target, builders);
      const changeSetId = outcome.kind === "proposed" ? outcome.changeSetId : unanswered;
      if (changeSetId === null) return { kind: "nothingToChange" };
      if (reviewedIn !== null && reviewedIn !== shownIn) return { kind: "answered", changeSetId, threadId: reviewedIn };
      unanswered = changeSetId;
      const threadId = await answer(changeSetId);
      unanswered = null;
      return { kind: "answered", changeSetId, threadId };
    },
  };
}

/**
 * No edit step: a new item's form (ADR-0031 D3's "by hand" is its own Workflow, not built yet)
 * and a part saved inside a larger edit, such as a step made in the Workflow editor, whose own
 * save answers the step. It commits only.
 */
export const COMMIT_ONLY: ChangeSetStep = {
  shownIn: null,
  save: async (projectId, choice, builders) => {
    const outcome = await commitToChangeSet(projectId, choice, builders);
    return outcome.kind === "proposed" ? { kind: "committed", changeSetId: outcome.changeSetId } : outcome;
  },
};

export const ChangeSetStepContext = createContext<ChangeSetStep>(COMMIT_ONLY);
